import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * FIX #5 + #8: Reusable password-confirmation modal.
 *
 * Usage:
 *   const [confirmState, setConfirmState] = useState(null);
 *   // Trigger:
 *   setConfirmState({
 *     title: 'Delete all notifications',
 *     message: 'This will permanently remove every notification on your account.',
 *     onConfirm: async (sensitiveActionToken) => {
 *       await axios.delete(`${API_URL}/api/notifications`, {
 *         headers: {
 *           Authorization: `Bearer ${token}`,
 *           'X-Sensitive-Token': sensitiveActionToken,
 *         },
 *       });
 *     },
 *   });
 *   // Render:
 *   {confirmState && (
 *     <PasswordConfirmModal
 *       title={confirmState.title}
 *       message={confirmState.message}
 *       authToken={token}
 *       onConfirm={confirmState.onConfirm}
 *       onCancel={() => setConfirmState(null)}
 *     />
 *   )}
 *
 * The modal (a) asks for the user's password, (b) calls /api/auth/confirm-password
 * to get a short-lived sensitiveActionToken, (c) invokes the caller's onConfirm
 * with that token, (d) closes on success. The caller's delete request must
 * include `X-Sensitive-Token` in its headers.
 */
export default function PasswordConfirmModal({
  title = 'Confirm your password',
  message = 'Enter your password to continue.',
  confirmButtonLabel = 'Confirm',
  authToken,
  onConfirm,
  onCancel,
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Autofocus on mount for fast keyboard-driven confirmation.
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    // Esc closes; Enter submits (handled by form).
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) { setError('Please enter your password.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/confirm-password`,
        { password },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const sensitiveActionToken = res.data?.sensitiveActionToken;
      if (!sensitiveActionToken) throw new Error('No confirmation token received');
      // Hand the short-lived token back to the caller. The caller is responsible
      // for sending it with the actual destructive request.
      await onConfirm(sensitiveActionToken);
      setPassword('');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Could not verify your password.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pw-confirm-title"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xl flex-shrink-0" aria-hidden="true">🔒</div>
          <div className="flex-1 min-w-0">
            <h2 id="pw-confirm-title" className="text-xl font-bold text-[#294c79]">{title}</h2>
            <p className="text-sm text-[#1a1a1a]/60 mt-1">{message}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block">
            <span className="block text-sm font-semibold text-[#294c79] mb-2">Password</span>
            <div className="relative">
              <input
                ref={inputRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter your account password"
                autoComplete="current-password"
                className="w-full px-4 py-3 pr-12 rounded-xl border border-[#d9e5ef] focus:outline-none focus:ring-2 focus:ring-[#294c79]/30 focus:border-[#294c79]"
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#294c79] font-medium hover:underline"
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm" role="alert">
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 rounded-xl border border-[#d9e5ef] text-[#294c79] font-semibold hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !password}
              className="px-5 py-2.5 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying…' : confirmButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
